import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Field, inputCls, areaCls, ChoiceGroup } from "../components/FormBits";
import { THEMES, themeBySlug, onColor } from "../data/content";
import { api, errorMessage } from "../lib/api";

const SIZES = [
  { value: "1-20", label: "1 à 20 utilisateurs" },
  { value: "21-100", label: "21 à 100 utilisateurs" },
  { value: "101-500", label: "101 à 500 utilisateurs" },
  { value: "500+", label: "Plus de 500 utilisateurs" },
];
const TIMELINES = ["Dès que possible", "Dans le mois", "Dans les 3 mois", "Je me renseigne"];
const STEP_NAMES = ["Votre besoin", "Votre entreprise", "Vos coordonnées"];

const Progress = ({ step, color }) => (
  <div className="flex gap-2 mb-10" data-testid="quote-progress">
    {STEP_NAMES.map((n, i) => (
      <div key={n} className="flex-1">
        <span className="block h-1.5 transition-colors duration-500" style={{ background: i <= step ? color : "rgba(62,62,62,.12)" }} />
        <span className={`hidden sm:block text-xs mt-2 font-semibold uppercase tracking-[0.14em] ${i === step ? "text-ink" : "text-ink/40"}`}>{i + 1}. {n}</span>
      </div>
    ))}
  </div>
);

export default function Devis() {
  const [params] = useSearchParams();
  const pre = themeBySlug(params.get("service"));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({ services: pre ? [pre.slug] : [], details: "", company: "", company_size: "", timeline: "", name: "", email: "", phone: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const color = themeBySlug(f.services[0])?.color || "#45ADE0";

  const next = () => {
    if (step === 0 && f.services.length === 0) return toast.error("Choisissez au moins un besoin.");
    if (step === 1 && (!f.company || !f.company_size)) return toast.error("Indiquez le nom et la taille de votre structure.");
    setStep(step + 1);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", f);
      setDone(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHero eyebrow="Demande de devis" color={color} title="Votre devis en 3 étapes." lead="Deux minutes pour nous décrire votre besoin. Un conseiller revient vers vous avec une proposition claire." />
      <section className="mx-auto max-w-3xl px-5 lg:px-8 py-16 lg:py-24">
        {done ? (
          <div data-testid="quote-success" className="bg-white border-2 p-10" style={{ borderColor: color }}>
            <CheckCircle2 size={40} style={{ color }} />
            <h2 className="font-display text-3xl font-bold mt-5">Merci {done.name}, c'est noté.</h2>
            <p className="text-ink/65 mt-3">Votre demande <strong data-testid="quote-ref">#{done.id.slice(-6).toUpperCase()}</strong> ({done.services.map((s) => themeBySlug(s)?.name).join(", ")}) a bien été envoyée à notre équipe commerciale. Un e-mail de confirmation vous a été adressé. Un conseiller vous recontacte au {done.phone}.</p>
            <Link to="/realisations/references" className="btn-outline mt-8" data-testid="quote-success-cases-link">Voir nos réalisations <ArrowRight size={16} /></Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="quote-form">
            <Progress step={step} color={color} />
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }} className="space-y-6">
                {step === 0 && (
                  <>
                    <h2 className="font-display text-2xl font-bold">De quoi avez-vous besoin ?</h2>
                    <ChoiceGroup multi prefix="quote-service" value={f.services} onChange={set("services")} options={THEMES.map((t) => ({ value: t.slug, label: t.name, desc: t.short, c: t.color }))} />
                    <Field label="Précisions (facultatif)"><textarea data-testid="quote-details-input" className={areaCls} value={f.details} onChange={set("details")} placeholder="Nombre de postes, de bureaux, matériel existant…" /></Field>
                  </>
                )}
                {step === 1 && (
                  <>
                    <h2 className="font-display text-2xl font-bold">Parlez-nous de votre structure.</h2>
                    <Field label="Nom de l'entreprise"><input data-testid="quote-company-input" className={inputCls} value={f.company} onChange={set("company")} /></Field>
                    <Field label="Taille"><ChoiceGroup prefix="quote-size" color={color} value={f.company_size} onChange={set("company_size")} options={SIZES} /></Field>
                    <Field label="Échéance">
                      <select data-testid="quote-timeline-select" className={inputCls} value={f.timeline} onChange={set("timeline")}>
                        <option value="">Choisir…</option>
                        {TIMELINES.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </Field>
                  </>
                )}
                {step === 2 && (
                  <>
                    <h2 className="font-display text-2xl font-bold">Comment vous joindre ?</h2>
                    <Field label="Nom et prénom"><input data-testid="quote-name-input" className={inputCls} value={f.name} onChange={set("name")} required /></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="E-mail"><input type="email" data-testid="quote-email-input" className={inputCls} value={f.email} onChange={set("email")} required /></Field>
                      <Field label="Téléphone"><input type="tel" data-testid="quote-phone-input" className={inputCls} value={f.phone} onChange={set("phone")} required minLength={6} /></Field>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="mt-10 flex justify-between gap-3">
              {step > 0 ? (
                <button type="button" data-testid="quote-back-btn" onClick={() => setStep(step - 1)} className="btn-outline"><ArrowLeft size={16} /> Retour</button>
              ) : <span />}
              {step < 2 ? (
                <button type="button" data-testid="quote-next-btn" onClick={next} className="btn-primary" style={{ background: color, color: onColor(color) }}>Continuer <ArrowRight size={16} /></button>
              ) : (
                <button type="submit" disabled={busy} data-testid="quote-submit-btn" className="btn-primary" style={{ background: color, color: onColor(color) }}>
                  {busy && <Loader2 className="animate-spin" size={16} />} Envoyer ma demande
                </button>
              )}
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
